// Sidebar.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Space,
  Typography,
  Input,
  Avatar,
  Dropdown,
  Menu,
  Badge,
  Tooltip,
  Divider
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  TeamOutlined,
  RocketOutlined,
  MoreOutlined,
  ExportOutlined,
  ImportOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

const Sidebar = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  searchTerm,
  setSearchTerm,
  filterAssignee,
  setFilterAssignee,
  filterPriority,
  setFilterPriority,
  boardStats,
  filteredTasks,
  tasks,
  handleClearSearch,
  handleAddTask,
  handleExportData,
  users,
  showControls = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Detect mobile viewport to switch to overlay behavior
  const [isMobile, setIsMobile] = useState(false);
  const [activeTop, setActiveTop] = useState('');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/kanban-board')) {
      setActiveTop('kanban');
    } else if (location.pathname.startsWith('/equipment')) {
      setActiveTop('equipment');
    } else if (location.pathname.startsWith('/analytics')) {
      setActiveTop('reports');
    }
  }, [location.pathname]);

  const sidebarWidth = sidebarCollapsed ? (isMobile ? 0 : 80) : (isMobile ? Math.min(window.innerWidth, 320) : 300);
  const sidebarVisible = !(isMobile && sidebarCollapsed);

  // Convert Generate Reports to a direct button (no dropdown)

  const assigneeMenu = (
    <Menu
      selectedKeys={[filterAssignee]}
      items={[
        {
          key: '',
          label: 'All Assignees',
          onClick: () => setFilterAssignee('')
        },
        ...users.map(user => ({
          key: user,
          label: user,
          onClick: () => setFilterAssignee(user)
        }))
      ]}
    />
  );

  const priorityMenu = (
    <Menu
      selectedKeys={[filterPriority]}
      items={[
        {
          key: '',
          label: 'All Priorities',
          onClick: () => setFilterPriority('')
        },
        {
          key: 'Critical',
          label: 'Critical',
          style: { color: '#ff4d4f' }
        },
        {
          key: 'High',
          label: 'High',
          style: { color: '#fa8c16' }
        },
        {
          key: 'Medium',
          label: 'Medium',
          style: { color: '#faad14' }
        },
        {
          key: 'Low',
          label: 'Low',
          style: { color: '#52c41a' }
        }
      ].map(item => ({
        ...item,
        onClick: () => setFilterPriority(item.key)
      }))}
    />
  );

  return (
    <>
      {isMobile && !sidebarCollapsed && (
        <div
          onClick={() => setSidebarCollapsed(true)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            zIndex: 998
          }}
        />
      )}
      <div style={{
        width: `${sidebarWidth}px`,
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        color: '#262626',
        padding: sidebarCollapsed ? '16px 8px' : '20px 16px',
        transition: 'all 0.3s ease',
        display: sidebarVisible ? 'flex' : 'none',
        flexDirection: 'column',
        height: isMobile ? '100vh' : '100%',
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        zIndex: isMobile ? 999 : 'auto',
        boxShadow: isMobile ? '0 8px 24px rgba(0,0,0,0.25)' : '2px 0 12px rgba(0,0,0,0.08)',
        flexShrink: 0,
        border: isMobile ? 'none' : '2px solid #e8e8e8',
        borderRight: isMobile ? 'none' : '2px solid #d9d9d9',
        borderRadius: isMobile ? '0' : '16px',
        margin: isMobile ? '0' : '8px',
        marginRight: isMobile ? '0' : '0',
        overflowY: 'auto',
        overscrollBehavior: 'contain'
      }}>
      {/* هدر سایدبار */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: sidebarCollapsed ? 'center' : 'space-between',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #f0f0f0',
        borderRadius: '8px'
      }}>
        {!sidebarCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RocketOutlined style={{
              fontSize: '24px',
              color: '#1890ff'
            }} />
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600',
                color: '#262626'
              }}>
                Project Board
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#8c8c8c',
                marginTop: '2px'
              }}>
                Solar Power Plant
              </div>
            </div>
          </div>
        )}
        
        <Button
          type="text"
          icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{
            color: '#666666',
            fontSize: '16px',
            border: '1px solid #f0f0f0',
            borderRadius: '8px',
            width: '32px',
            height: '32px'
          }}
        />
      </div>

      {/* Top navigation buttons */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }} size={8}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<DashboardOutlined />}
            onClick={() => { setActiveTop('kanban'); navigate('/kanban-board'); }}
            size="middle"
            style={{
              flex: 1,
              background: activeTop === 'kanban' ? '#e6f7ff' : 'white',
              border: `2px solid ${activeTop === 'kanban' ? '#1890ff' : '#e8e8e8'}`,
              color: activeTop === 'kanban' ? '#1890ff' : '#595959',
              fontWeight: activeTop === 'kanban' ? 600 : 400,
              borderRadius: '10px',
              height: '40px'
            }}
          >
            Kanban
          </Button>
          <Button
            icon={<DashboardOutlined />}
            onClick={() => { setActiveTop('equipment'); navigate('/equipment'); }}
            size="middle"
            style={{
              flex: 1,
              background: activeTop === 'equipment' ? '#e6fffb' : 'white',
              border: `2px solid ${activeTop === 'equipment' ? '#13c2c2' : '#e8e8e8'}`,
              color: activeTop === 'equipment' ? '#13c2c2' : '#595959',
              fontWeight: activeTop === 'equipment' ? 600 : 400,
              borderRadius: '10px',
              height: '40px'
            }}
          >
            Equipment
          </Button>
        </div>
        <Button
          icon={<BarChartOutlined />}
          onClick={() => { setActiveTop('reports'); navigate('/analytics'); }}
          size="middle"
          style={{
            width: '100%',
            background: activeTop === 'reports' ? '#fff7e6' : 'white',
            border: `2px solid ${activeTop === 'reports' ? '#fa8c16' : '#e8e8e8'}`,
            color: activeTop === 'reports' ? '#fa8c16' : '#595959',
            fontWeight: activeTop === 'reports' ? 600 : 400,
            borderRadius: '10px',
            height: '40px'
          }}
        >
          Analytics & KPI Dashboard
        </Button>
        <Divider style={{ margin: '8px 0' }} />
      </Space>

      {/* آمار کلی */}
      {showControls && !sidebarCollapsed && (
        <div style={{
          background: 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)',
          border: '1px solid #1890ff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <DashboardOutlined style={{ color: 'white', fontSize: '16px' }} />
            <Text strong style={{ color: 'white', fontSize: '14px' }}>Board Overview</Text>
          </div>
          
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>Total Tasks</Text>
              <Badge count={boardStats.totalTasks} style={{ 
                background: 'rgba(255,255,255,0.3)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '8px'
              }} />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>Completion</Text>
              <Text strong style={{ color: 'white', fontSize: '12px' }}>
                {boardStats.completionRate}%
              </Text>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>Overdue</Text>
              <Badge count={boardStats.overdueTasks} style={{ 
                background: 'rgba(255,77,79,0.8)',
                color: 'white',
                borderRadius: '8px'
              }} />
            </div>
          </Space>
        </div>
      )}

      {/* جستجو */}
      {showControls && (
        <div style={{ marginBottom: '20px' }}>
          {!sidebarCollapsed ? (
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
              size="middle"
              style={{
                borderRadius: '10px',
                border: '2px solid #e8e8e0',
                background: 'white',
                color: '#262626'
              }}
              allowClear
            />
          ) : (
            <Tooltip title="Search" placement="right">
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={() => setSidebarCollapsed(false)}
                style={{
                  color: '#666666',
                  width: '100%',
                  height: '40px',
                  border: '2px solid #f0f0f0',
                  borderRadius: '10px'
                }}
              />
            </Tooltip>
          )}
        </div>
      )}

      {/* فیلترها */}
      {showControls && !sidebarCollapsed && (
        <div style={{ marginBottom: '20px' }}>
          <Text strong style={{ color: '#595959', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
            FILTERS
          </Text>
          
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Dropdown overlay={assigneeMenu} placement="bottomLeft">
              <Button
                icon={<TeamOutlined />}
                size="middle"
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  background: 'white',
                  border: '2px solid #e8e8e8',
                  color: '#595959',
                  borderRadius: '10px',
                  height: '40px'
                }}
              >
                {filterAssignee || 'All Assignees'}
              </Button>
            </Dropdown>

            <Dropdown overlay={priorityMenu} placement="bottomLeft">
              <Button
                icon={<FilterOutlined />}
                size="middle"
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  background: 'white',
                  border: '2px solid #e8e8e8',
                  color: '#595959',
                  borderRadius: '10px',
                  height: '40px'
                }}
              >
                {filterPriority || 'All Priorities'}
              </Button>
            </Dropdown>
          </Space>
        </div>
      )}

      {/* آمار سریع حذف شد */}

      {/* تیم حذف شد */}

      {/* دکمه‌های اکشن */}
      <div style={{ marginTop: 'auto', paddingTop: '20px' }}>
        {!sidebarCollapsed ? (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {/* Nav buttons moved to top */}
            {showControls && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddTask}
                size="middle"
                style={{
                  width: '100%',
                  background: 'linear-gradient(45deg, #1890ff, #722ed1)',
                  border: 'none',
                  borderRadius: '12px',
                  height: '42px',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                }}
              >
                New Task
              </Button>
            )}
            {/* Removed More Actions */}
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {/* Nav buttons moved to top */}
            {showControls && (
              <Tooltip title="New Task" placement="right">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddTask}
                  size="middle"
                  style={{
                    width: '100%',
                    background: 'linear-gradient(45deg, #1890ff, #722ed1)',
                    border: 'none',
                    borderRadius: '12px',
                    height: '42px',
                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                  }}
                />
              </Tooltip>
            )}
            {/* Removed More Actions */}
          </Space>
        )}
      </div>

      {/* وضعیت فیلترها */}
      {showControls && (searchTerm || filterAssignee || filterPriority) && !sidebarCollapsed && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          background: '#e6f7ff',
          borderRadius: '10px',
          border: '2px solid #91d5ff'
        }}>
          <Text style={{ color: '#0050b3', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
            Active filters: {filteredTasks.length} tasks
          </Text>
          <Button
            type="link"
            size="small"
            onClick={handleClearSearch}
            style={{ 
              padding: 0, 
              height: 'auto', 
              fontSize: '10px',
              color: '#1890ff',
              fontWeight: '600'
            }}
          >
            Clear all
          </Button>
        </div>
      )}
      </div>
    </>
  );
};

export default Sidebar;